import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Gift, Copy, Check, Award, Download } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [certs, setCerts] = useState([]);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    api.get("/progress/me").then(({ data }) => setData(data));
    api.get("/certificates/me").then(({ data }) => setCerts(data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-10 font-mono-label text-muted-foreground">Loading…</div>;

  const totalLessons = data.courses.reduce((s, c) => s + c.total_lessons, 0);
  const totalDone = data.courses.reduce((s, c) => s + c.completed_lessons, 0);
  const overall = totalLessons ? Math.round((totalDone / totalLessons) * 100) : 0;

  const referralLink = user?.referral_code
    ? `${window.location.origin}/signup?ref=${user.referral_code}`
    : "";

  const copyLink = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadCertificate = async (courseId, courseTitle) => {
    setDownloading(courseId);
    try {
      const token = localStorage.getItem("dsa_token");
      const res = await fetch(`${API}/certificates/${courseId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Download failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Porcelain_${courseTitle.replace(/\s+/g, "_")}_Certificate.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
    } catch (err) {
      toast.error(err.message || "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  const earned = certs.filter((c) => c.eligible);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="dashboard-page">
      <div className="font-mono-label text-primary">Your Studio</div>
      <h1 className="font-serif-display text-5xl md:text-6xl mt-3 font-light leading-none tracking-tighter">
        Welcome back, {user?.name?.split(" ")[0]}
      </h1>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="porcelain-card p-6 md:col-span-2">
          <div className="font-mono-label text-muted-foreground">Overall Progress</div>
          <div className="flex items-end gap-4 mt-4">
            <div className="font-serif-display text-6xl leading-none">{overall}<span className="text-2xl">%</span></div>
            <div className="font-mono-label text-muted-foreground mb-2">{totalDone} / {totalLessons} LESSONS</div>
          </div>
          <div className="h-1 bg-secondary mt-6">
            <div className="h-full bg-primary transition-all duration-500" style={{ width: `${overall}%` }} />
          </div>
        </div>
        <div className="porcelain-card p-6">
          <div className="font-mono-label text-muted-foreground">Quiz Attempts</div>
          <div className="font-serif-display text-6xl leading-none mt-4">{data.quiz_attempts.length}</div>
          <div className="font-mono-label text-muted-foreground mt-2">ALL TIME</div>
        </div>
        <div className="porcelain-card p-6">
          <div className="font-mono-label text-muted-foreground">Avg. Quiz Score</div>
          <div className="font-serif-display text-6xl leading-none mt-4">
            {data.quiz_attempts.length
              ? Math.round(data.quiz_attempts.reduce((s, a) => s + a.percent, 0) / data.quiz_attempts.length)
              : 0}
            <span className="text-2xl">%</span>
          </div>
          <div className="font-mono-label text-muted-foreground mt-2">ASSESSMENT</div>
        </div>
      </div>

      <h2 className="font-serif-display text-3xl mt-16 mb-6 tracking-tight">Your courses</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.courses.map((c) => (
          <Link
            key={c.course_id}
            to={`/courses/${c.course_id}`}
            className="porcelain-card p-6 flex gap-6 group"
            data-testid={`dashboard-course-${c.course_id}`}
          >
            <img src={c.cover_image} alt={c.title} className="w-24 h-24 object-cover flex-shrink-0" style={{ borderRadius: 2 }} />
            <div className="flex-1">
              <div className="font-mono-label text-primary">{c.category}</div>
              <h3 className="font-serif-display text-2xl leading-none mt-1 tracking-tight">{c.title}</h3>
              <div className="mt-3 flex items-center gap-3">
                <div className="flex-1 h-1 bg-secondary">
                  <div className="h-full bg-primary" style={{ width: `${c.percent}%` }} />
                </div>
                <div className="font-mono-label text-muted-foreground">{c.percent}%</div>
              </div>
              <div className="mt-3 font-mono-label text-muted-foreground flex items-center gap-1 group-hover:text-primary">
                Continue <ArrowRight className="w-3 h-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {earned.length > 0 && (
        <>
          <h2 className="font-serif-display text-3xl mt-16 mb-6 tracking-tight flex items-center gap-3">
            <Award className="w-6 h-6 text-primary" /> Earned certificates
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dashboard-certificates">
            {earned.map((c) => (
              <div key={c.course_id} className="porcelain-card p-6 flex items-center justify-between" data-testid={`certificate-${c.course_id}`}>
                <div>
                  <div className="font-mono-label text-primary">{c.category} · 100% COMPLETE</div>
                  <h3 className="font-serif-display text-2xl leading-none mt-2 tracking-tight">{c.course_title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{c.course_subtitle}</p>
                </div>
                <button
                  onClick={() => downloadCertificate(c.course_id, c.course_title)}
                  disabled={downloading === c.course_id}
                  className="btn-oxblood text-sm inline-flex items-center gap-2 disabled:opacity-50 flex-shrink-0 ml-4"
                  data-testid={`certificate-download-${c.course_id}`}
                >
                  <Download className="w-4 h-4" /> {downloading === c.course_id ? "…" : "PDF"}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {user?.referral_code && (
        <div className="mt-16 porcelain-card p-8 grid grid-cols-1 md:grid-cols-12 gap-8 items-center" data-testid="dashboard-referral">
          <div className="md:col-span-8">
            <div className="font-mono-label text-primary flex items-center gap-2">
              <Gift className="w-3 h-3" /> Refer a student
            </div>
            <h2 className="font-serif-display text-3xl mt-3 leading-none tracking-tight">
              Share Porcelain. Unlock a bonus preview.
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl">
              When a fellow student signs up with your code, <strong>both of you</strong> get an extra free-preview lesson inside the Pro masterclasses.
              {user.has_referral_reward && <span className="block mt-2 text-primary font-medium">✓ Bonus preview already unlocked on your account.</span>}
            </p>
            <div className="mt-4 font-mono-label text-muted-foreground">
              {user.referral_count} SIGNUP{user.referral_count === 1 ? "" : "S"} SO FAR
            </div>
          </div>
          <div className="md:col-span-4 space-y-3">
            <div className="bg-background border border-border p-4 text-center" style={{ borderRadius: 2 }}>
              <div className="font-mono-label text-muted-foreground">YOUR CODE</div>
              <div className="font-mono text-3xl font-light tracking-widest mt-2" data-testid="dashboard-referral-code">{user.referral_code}</div>
            </div>
            <button
              onClick={copyLink}
              className="btn-oxblood w-full text-sm inline-flex items-center justify-center gap-2"
              data-testid="dashboard-referral-copy"
            >
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy share link</>}
            </button>
            <div className="font-mono-label text-muted-foreground text-[0.6rem] break-all text-center">{referralLink}</div>
          </div>
        </div>
      )}

      {data.quiz_attempts.length > 0 && (
        <>
          <h2 className="font-serif-display text-3xl mt-16 mb-6 tracking-tight">Recent quiz attempts</h2>
          <div className="border-t border-border">
            {data.quiz_attempts.slice(0, 6).map((a) => (
              <div key={a.id} className="border-b border-border py-5 flex items-center justify-between" data-testid={`attempt-${a.id}`}>
                <div>
                  <div className="font-serif-display text-xl leading-none">{a.quiz_title}</div>
                  <div className="font-mono-label text-muted-foreground mt-2">{new Date(a.created_at).toLocaleString()}</div>
                </div>
                <div className="font-mono text-3xl font-light">
                  <span className={a.percent >= 70 ? "text-primary" : "text-muted-foreground"}>{a.percent}%</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
